<template>
  <br/>
  <div class="row">
    <div class="alert alert-success" role="alert" v-if="successMessage">
      {{ successMessage }}
    </div>
    <div class="alert alert-danger" role="alert" v-if="errorMessage">
      {{ errorMessage }}
    </div>
    <div class="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
      <button type="button" class="btn btn-primary" data-bs-target="#createFouitaWidgetModal" data-bs-toggle="modal"
              :disabled="disableSubmit" @click="resetFouitaWidgetParams(); resetValidationErrors()">
        <span v-if="!disableSubmit">Create widget</span>
        <span v-else><font-awesome-icon icon="fa-solid fa-sync" spin/></span>
      </button>
    </div>
  </div>
  <div class="table-responsive">
    <table class="table">
      <thead>
      <tr>
        <th scope="col">Id</th>
        <th scope="col">Name</th>
        <th scope="col">Widget Id</th>
        <th scope="col">JS File URL</th>
        <th scope="col">Languages</th>
        <th scope="col">Auth state</th>
        <th scope="col">Status</th>
        <th scope="col">Action</th>
      </tr>
      </thead>
      <tbody>
      <tr v-if="totalRecords > 0" v-for="fouitaWidget in fouitaWidgets" :key="fouitaWidget.id">
        <td>{{ fouitaWidget.id }}</td>
        <td>{{ fouitaWidget.name }}</td>
        <td>{{ fouitaWidget.fouita_widget_id }}</td>
        <td>{{ truncateScript(fouitaWidget.fouita_js_file_url) }}</td>
        <td>{{ fouitaWidget.selected_languages ? fouitaWidget.selected_languages.join(', ') : '' }}</td>
        <td>{{ authStateLabel(fouitaWidget.auth_state) }}</td>
        <td>
          <span :class="statusBadgeClass(fouitaWidget.status)">{{ statusLabel(fouitaWidget.status) }}</span>
        </td>
        <td>
          <div class="btn-group-sm d-flex gap-3" role="group">
            <button type="button" class="btn btn-info" data-bs-target="#updateFouitaWidgetModal" data-bs-toggle="modal"
                    :disabled="disableSubmit" @click="openUpdateFouitaWidgetModal(fouitaWidget.id)">
              <span v-if="!disableSubmit">Edit</span>
              <span v-else><font-awesome-icon icon="fa-solid fa-sync" spin/></span>
            </button>
            <button type="button" class="btn btn-danger" :disabled="disableSubmit"
                    @click="deleteFouitaWidget(fouitaWidget.id, fouitaWidget.name)">
              Delete
            </button>
          </div>
        </td>
      </tr>
      <tr v-else-if="loading">
        <td colspan="8" class="text-center">
          <font-awesome-icon icon="fa-solid fa-sync" spin/>
        </td>
      </tr>
      <tr v-else>
        <td colspan="8" class="text-center">No widgets</td>
      </tr>
      </tbody>
    </table>
    <div class="d-flex justify-content-between" v-if="totalRecords >= 1">
      <div>
        <div class="p-2">
          <p>Total widgets: {{ totalRecords }}</p>
        </div>
      </div>
    </div>
  </div>

  <div class="modal fade" id="createFouitaWidgetModal" data-bs-backdrop="static" data-bs-keyboard="false"
       tabindex="-1"
       aria-labelledby="createStaticBackdropLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h1 class="modal-title fs-5" id="createStaticBackdropLabel">Create Fouita widget form</h1>
          <button
              id="closeCreateFouitaWidgetBtn"
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close">
          </button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label for="createFouitaWidgetName" class="form-label">Name</label>
            <input type="text" class="form-control me-1" id="createFouitaWidgetName"
                   v-model="createFouitaWidgetName">
            <div v-if="validationErrors" v-for="error in validationErrors.name" class="alert alert-danger mt-2" role="alert">{{ error }}</div>
          </div>
          <div class="mb-3">
            <label for="createFouitaWidgetId" class="form-label">Widget Id</label>
            <input type="text" class="form-control me-1" id="createFouitaWidgetId"
                   v-model="createFouitaWidgetId">
            <div v-if="validationErrors" v-for="error in validationErrors['fouita_widget_id']" class="alert alert-danger mt-2" role="alert">{{ error }}</div>
          </div>
          <div class="mb-3">
            <label for="createFouitaJsFileUrl" class="form-label">JS File URL</label>
            <input type="text" class="form-control me-1" id="createFouitaJsFileUrl"
                   v-model="createFouitaJsFileUrl">
            <div v-if="validationErrors" v-for="error in validationErrors['fouita_js_file_url']" class="alert alert-danger mt-2" role="alert">{{ error }}</div>
          </div>

          <div class="mb-3">
            <div class="alert alert-warning">
              <p>To get the <strong>Widget Id</strong> and the <strong>JS File URL</strong>, log in to your Fouita dashboard, go to <strong>Widgets</strong>, then create a new widget or open an existing one and click <strong>Embed</strong>. Both values are in the embed code shown there.</p>
            </div>
            <img class="w-100" src="fouita-explain.png" alt="Fouita explain"/>
            <img class="w-100" src="fouita-embed.png" alt="Fouita embed"/>
          </div>

          <div class="mb-3">
            <label class="form-label">Languages</label>
            <div class="d-flex flex-wrap gap-3">
              <div class="form-check" v-for="language in languages" :key="language.value">
                <input class="form-check-input" type="checkbox" :id="`createLanguage-${language.value}`"
                       :value="language.value" v-model="createFouitaWidgetSelectedLanguages">
                <label class="form-check-label" :for="`createLanguage-${language.value}`">{{ language.name }}</label>
              </div>
            </div>
            <div v-if="validationErrors" v-for="error in selectedLanguagesErrors" class="alert alert-danger mt-2" role="alert">{{ error }}</div>
          </div>
          <div class="mb-3">
            <label for="createFouitaWidgetAuthState" class="form-label">Auth state</label>
            <select class="form-control" id="createFouitaWidgetAuthState" v-model="createFouitaWidgetAuthState">
              <option v-for="authState in authStates" :key="authState.value" :value="authState.value">{{ authState.name }}</option>
            </select>
            <div v-if="validationErrors" v-for="error in validationErrors['auth_state']" class="alert alert-danger mt-2" role="alert">{{ error }}</div>
          </div>
          <div class="mb-3">
            <label for="createFouitaWidgetStatus" class="form-label">Status</label>
            <select class="form-control" id="createFouitaWidgetStatus" v-model="createFouitaWidgetStatus">
              <option v-for="status in statuses" :key="status.value" :value="status.value">{{ status.name }}</option>
            </select>
            <div v-if="validationErrors" v-for="error in validationErrors['status']" class="alert alert-danger mt-2" role="alert">{{ error }}</div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="createFouitaWidget"
                  type="button" class="btn btn-dark-purple" :disabled="disableSubmit">
            <span v-if="!disableSubmit">Create</span>
            <span v-else><font-awesome-icon icon="fa-solid fa-sync" spin/></span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="modal fade" id="updateFouitaWidgetModal" data-bs-backdrop="static" data-bs-keyboard="false"
       tabindex="-1"
       aria-labelledby="updateStaticBackdropLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h1 class="modal-title fs-5" id="updateStaticBackdropLabel">Update Fouita widget form
            <span v-if="disableSubmit">
              <font-awesome-icon icon="fa-solid fa-sync" spin/>
            </span>
          </h1>
          <button id="closeUpdateFouitaWidgetBtn" type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label for="updateFouitaWidgetName" class="form-label">Name</label>
            <input type="text" class="form-control me-1" id="updateFouitaWidgetName" v-model="updateFouitaWidgetName">
            <div v-if="validationErrors" v-for="error in validationErrors.name" class="alert alert-danger mt-2" role="alert">{{ error }}</div>
          </div>
          <div class="mb-3">
            <label for="updateFouitaWidgetId" class="form-label">Widget Id</label>
            <input type="text" class="form-control me-1" id="updateFouitaWidgetId"
                   v-model="updateFouitaWidgetWidgetId">
            <div v-if="validationErrors" v-for="error in validationErrors['fouita_widget_id']" class="alert alert-danger mt-2" role="alert">{{ error }}</div>
          </div>
          <div class="mb-3">
            <label for="updateFouitaJsFileUrl" class="form-label">JS File URL</label>
            <input type="text" class="form-control me-1" id="updateFouitaJsFileUrl"
                   v-model="updateFouitaJsFileUrl">
            <div v-if="validationErrors" v-for="error in validationErrors['fouita_js_file_url']" class="alert alert-danger mt-2" role="alert">{{ error }}</div>
          </div>
          <div class="mb-3">
            <label class="form-label">Languages</label>
            <div class="d-flex flex-wrap gap-3">
              <div class="form-check" v-for="language in languages" :key="language.value">
                <input class="form-check-input" type="checkbox" :id="`updateLanguage-${language.value}`"
                       :value="language.value" v-model="updateFouitaWidgetSelectedLanguages">
                <label class="form-check-label" :for="`updateLanguage-${language.value}`">{{ language.name }}</label>
              </div>
            </div>
            <div v-if="validationErrors" v-for="error in selectedLanguagesErrors" class="alert alert-danger mt-2" role="alert">{{ error }}</div>
          </div>
          <div class="mb-3">
            <label for="updateFouitaWidgetAuthState" class="form-label">Auth state</label>
            <select class="form-control" id="updateFouitaWidgetAuthState" v-model="updateFouitaWidgetAuthState">
              <option v-for="authState in authStates" :key="authState.value" :value="authState.value">{{ authState.name }}</option>
            </select>
            <div v-if="validationErrors" v-for="error in validationErrors['auth_state']" class="alert alert-danger mt-2" role="alert">{{ error }}</div>
          </div>
          <div class="mb-3">
            <label for="updateFouitaWidgetStatus" class="form-label">Status</label>
            <select class="form-control" id="updateFouitaWidgetStatus" v-model="updateFouitaWidgetStatus">
              <option v-for="status in statuses" :key="status.value" :value="status.value">{{ status.name }}</option>
            </select>
            <div v-if="validationErrors" v-for="error in validationErrors['status']" class="alert alert-danger mt-2" role="alert">{{ error }}</div>
          </div>
        </div>
        <div class="modal-footer">
          <button
              @click="updateFouitaWidget"
              type="button" class="btn btn-dark-purple" :disabled="disableSubmit">
            <span v-if="!disableSubmit">Update</span>
            <span v-else><font-awesome-icon icon="fa-solid fa-sync" spin/></span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import axios from 'axios';
import Swal from "sweetalert2";

export default {
  data() {
    return {
      loading: true,
      fouitaWidgets: [],
      validationErrors: [],
      totalRecords: 0,
      disableSubmit: false,
      successMessage: "",
      errorMessage: "",
      languages: [
        {value: 'en', name: 'English'},
        {value: 'es', name: 'Spanish'},
        {value: 'it', name: 'Italian'},
        {value: 'lt', name: 'Lithuanian'},
        {value: 'ro', name: 'Romanian'},
        {value: 'ua', name: 'Ukrainian'},
        {value: 'lv', name: 'Latvian'},
        {value: 'et', name: 'Estonian'},
      ],
      authStates: [
        {value: 'logged_in', name: 'Logged in'},
        {value: 'guest', name: 'Guest'},
      ],
      statuses: [
        {value: 'active', name: 'Active'},
        {value: 'inactive', name: 'Inactive'},
      ],
      createFouitaWidgetName: "",
      createFouitaWidgetId: "",
      createFouitaJsFileUrl: "",
      createFouitaWidgetSelectedLanguages: [],
      createFouitaWidgetAuthState: "guest",
      createFouitaWidgetStatus: "active",
      updateFouitaWidgetId: "",
      updateFouitaWidgetName: "",
      updateFouitaWidgetWidgetId: "",
      updateFouitaJsFileUrl: "",
      updateFouitaWidgetSelectedLanguages: [],
      updateFouitaWidgetAuthState: "guest",
      updateFouitaWidgetStatus: "active"
    }
  },

  mounted() {
    this.getFouitaWidgets();
  },

  computed: {
    selectedLanguagesErrors() {
      if (!this.validationErrors) {
        return [];
      }
      return Object.keys(this.validationErrors)
          .filter(key => key === 'selected_languages' || key.startsWith('selected_languages.'))
          .flatMap(key => this.validationErrors[key]);
    }
  },

  methods: {
    closeModal() {
      document.querySelector('#closeUpdateFouitaWidgetBtn').click();
      document.querySelector('#closeCreateFouitaWidgetBtn').click();
    },
    getFouitaWidgets() {
      this.loading = true;
      axios.get(`/admin/fouita-widgets`)
          .then(response => {
            this.fouitaWidgets = response.data;
            this.totalRecords = response.data.length;
            this.loading = false;
          })
          .catch(error => {
            this.loading = false;
            console.log(error);
          });
    },
    truncateScript(url) {
      if (!url) {
        return '';
      }
      return url.length > 60 ? `${url.slice(0, 60)}...` : url;
    },
    authStateLabel(authState) {
      const match = this.authStates.find(state => state.value === authState);
      return match ? match.name : authState;
    },
    statusLabel(status) {
      const match = this.statuses.find(item => item.value === status);
      return match ? match.name : status;
    },
    statusBadgeClass(status) {
      return status === 'active' ? 'badge badge-success' : 'badge badge-secondary';
    },
    openUpdateFouitaWidgetModal(id) {
      this.disableSubmit = true;
      this.resetValidationErrors();
      axios.get(`/admin/fouita-widgets/${id}`)
          .then(response => {
            let responseData = response.data;
            this.updateFouitaWidgetId = responseData.id;
            this.updateFouitaWidgetName = responseData.name;
            this.updateFouitaWidgetWidgetId = responseData.fouita_widget_id;
            this.updateFouitaJsFileUrl = responseData.fouita_js_file_url;
            this.updateFouitaWidgetSelectedLanguages = responseData.selected_languages ?? [];
            this.updateFouitaWidgetAuthState = responseData.auth_state;
            this.updateFouitaWidgetStatus = responseData.status;
            this.disableSubmit = false;
          })
          .catch(error => {
            console.log(error);
            this.disableSubmit = false;
          });
    },
    createFouitaWidget() {
      this.disableSubmit = true;
      axios.post('/admin/fouita-widgets', {
        'name': this.createFouitaWidgetName,
        'fouita_widget_id': this.createFouitaWidgetId,
        'fouita_js_file_url': this.createFouitaJsFileUrl,
        'selected_languages': this.createFouitaWidgetSelectedLanguages,
        'auth_state': this.createFouitaWidgetAuthState,
        'status': this.createFouitaWidgetStatus,
      })
          .then(response => {
            this.successMessage = "Widget created successfully";
            this.getFouitaWidgets();
            this.resetFouitaWidgetParams();
            this.resetValidationErrors();
            this.disableSubmit = false;
            this.closeModal();
            setTimeout(() => this.successMessage = '', 3000);
          })
          .catch(error => {
            if (error.response?.status === 422) {
              this.validationErrors = error.response.data.errors;
            } else {
              this.errorMessage = error.response?.data?.message || 'Error saving the widget';
              setTimeout(() => this.errorMessage = '', 3000);
            }
            this.disableSubmit = false;
          });
    },
    updateFouitaWidget() {
      this.disableSubmit = true;
      axios.patch(`/admin/fouita-widgets/${this.updateFouitaWidgetId}`, {
        'name': this.updateFouitaWidgetName,
        'fouita_widget_id': this.updateFouitaWidgetWidgetId,
        'fouita_js_file_url': this.updateFouitaJsFileUrl,
        'selected_languages': this.updateFouitaWidgetSelectedLanguages,
        'auth_state': this.updateFouitaWidgetAuthState,
        'status': this.updateFouitaWidgetStatus,
      })
          .then(response => {
            this.successMessage = "Widget updated successfully";
            this.getFouitaWidgets();
            this.resetUpdateFouitaWidgetParams();
            this.resetValidationErrors();
            this.disableSubmit = false;
            this.closeModal();
            setTimeout(() => this.successMessage = '', 3000);
          })
          .catch(error => {
            if (error.response?.status === 422) {
              this.validationErrors = error.response.data.errors;
            } else {
              this.errorMessage = error.response?.data?.message || 'Error saving the widget';
              setTimeout(() => this.errorMessage = '', 3000);
            }
            this.disableSubmit = false;
          });
    },
    async deleteFouitaWidget(id, name) {
      const confirmed = await Swal.fire({
        title: 'Confirmation of deletion',
        text: `You are sure you want to delete the widget "${name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6'
      });

      if (confirmed.isConfirmed) {
        this.disableSubmit = true;

        try {
          await axios.delete(`/admin/fouita-widgets/${id}`);

          this.successMessage = `Widget "${name}" successfully deleted`;
          this.getFouitaWidgets();
          setTimeout(() => this.successMessage = '', 3000);
        } catch (error) {
          console.log(error);
          this.errorMessage = error.response?.data?.message || 'Error deleting the widget';
          setTimeout(() => this.errorMessage = '', 3000);
        } finally {
          this.disableSubmit = false;
        }
      }
    },
    resetUpdateFouitaWidgetParams() {
      this.updateFouitaWidgetId = "";
      this.updateFouitaWidgetName = "";
      this.updateFouitaWidgetWidgetId = "";
      this.updateFouitaJsFileUrl = "";
      this.updateFouitaWidgetSelectedLanguages = [];
      this.updateFouitaWidgetAuthState = "guest";
      this.updateFouitaWidgetStatus = "active";
    },
    resetFouitaWidgetParams() {
      this.createFouitaWidgetName = "";
      this.createFouitaWidgetId = "";
      this.createFouitaJsFileUrl = "";
      this.createFouitaWidgetSelectedLanguages = [];
      this.createFouitaWidgetAuthState = "guest";
      this.createFouitaWidgetStatus = "active";
    },
    resetValidationErrors() {
      this.validationErrors = [];
    }
  }

}
</script>
